import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { controlPlane } from '../lib/client'
import { rfStore } from '../lib/rfstore'
import { MORSE_TABLE } from '../lib/morse'
import { useI18n } from '../lib/i18n'

/** Velocidades em palavras por minuto — degraus, mesmo padrão de Filtro/Squelch. */
const WPM_NIVEIS = [10, 15, 20, 25] as const

/** Teto de caracteres por "over" — espelha internal/channel/hub.go:maxCWTextLen. */
const MAX_TEXTO = 120

type ModoEnvio = 'simples' | 'avancado' | 'chave'

/**
 * Envio de CW: "digita e manda", não "segure para falar" — no modo SIMPLES
 * (padrão). O tom sai de verdade pela trilha (AudioEngine.playTone com
 * transmit=true, mesmo mecanismo dos bipes de roger) — quem já sabe ler
 * Morse por ouvido pode simplesmente ouvir. `CWReadout`, ao lado, mostra a
 * cópia decodificada que o servidor manda pra quem ainda não sabe.
 *
 * O modo AVANÇADO troca "digita tudo, clica Enviar" por "cada tecla já sai
 * no ar na hora": `ControlPlane.startCWLive` abre a transmissão sem texto,
 * e cada caractere novo vira `queueCWChars`.
 *
 * O modo CHAVE MANUAL é o mais autêntico: sem teclado nenhum, um botão só
 * — segura pra ligar o tom, solta pra desligar. A DURAÇÃO do aperto decide
 * ponto ou traço (`ManualKeyer`, em lib/morse.ts), e é o app que decodifica
 * de volta em letra, não o contrário. É o que tira o "jeito de chat de
 * texto" que digitar sempre vai ter, mesmo no modo Avançado.
 *
 * Nos três modos, `CWReadout` do outro lado cresce sozinho a partir do
 * texto que vai sendo mandado por `cw_update` (o servidor recalcula
 * `RXParams.cw_text` a cada tick), e `CWEnviado` mostra pro próprio
 * operador o que já foi efetivamente transmitido, não só o que foi
 * digitado/apertado.
 *
 * O quadro de referência (colapsável, `<details>` nativo — sem estado novo)
 * é o que faz isto virar material de estudo, não só um efeito sonoro.
 */
export function CWSender({ powerW = 100 }: { powerW?: number }) {
  const { t } = useI18n()
  /** Três jeitos de mandar CW, do mais acessível ao mais autêntico. Mesmo
   * padrão de ciclo já usado pra WPM/Filtro/Squelch. */
  const MODOS_ENVIO = [
    { id: 'simples', nome: t.cwSender.modoSimples },
    { id: 'avancado', nome: t.cwSender.modoAvancado },
    { id: 'chave', nome: t.cwSender.modoChave },
  ] as const
  const [texto, setTexto] = useState('')
  const [wpm, setWpm] = useState<(typeof WPM_NIVEIS)[number]>(15)
  const [modo, setModo] = useState<ModoEnvio>('simples')

  const state = useSyncExternalStore(rfStore.subscribe, () => rfStore.snapshot())
  const podeTx = state.channel?.can_transmit ?? false
  const enviando = state.transmitting

  const enviar = () => {
    const t = texto.trim()
    if (!t || !podeTx || enviando) return
    navigator.vibrate?.(12)
    controlPlane.sendCW(t, wpm, powerW)
  }

  const cancelar = () => {
    navigator.vibrate?.(8)
    controlPlane.cancelCW()
    setTexto('')
  }

  // Modo avançado: só enfileira o que foi ACRESCENTADO ao final do campo.
  // Editar/apagar dentro do que já foi enfileirado não tenta desfazer o que
  // já tocou — mesma limitação de um telégrafo de verdade.
  const digitarAvancado = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value
    const anterior = texto
    setTexto(v)
    if (!podeTx || !v.startsWith(anterior)) return
    const novo = v.slice(anterior.length)
    if (!novo) return
    if (!enviando) controlPlane.startCWLive(wpm, powerW)
    controlPlane.queueCWChars(novo)
  }

  // Chave manual: startManualKey já se protege contra reabrir uma sessão em
  // curso, então dá pra chamar em TODO aperto sem checar `enviando` antes —
  // evita uma corrida com o estado do React, que só chega depois do
  // tx_granted ir e voltar do servidor.
  const apertarChave = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!podeTx) return
    e.currentTarget.setPointerCapture(e.pointerId)
    navigator.vibrate?.(12)
    controlPlane.startManualKey(wpm, powerW)
    controlPlane.keyDown()
  }
  const soltarChave = () => {
    navigator.vibrate?.(8)
    controlPlane.keyUp()
  }

  const proximoModo = () => {
    if (enviando) return
    const i = MODOS_ENVIO.findIndex((m) => m.id === modo)
    const prox = MODOS_ENVIO[(i + 1) % MODOS_ENVIO.length] ?? MODOS_ENVIO[0]
    setModo(prox.id)
    setTexto('')
  }

  const proximoWpm = () => {
    const i = WPM_NIVEIS.indexOf(wpm)
    setWpm(WPM_NIVEIS[(i + 1) % WPM_NIVEIS.length] ?? WPM_NIVEIS[0])
  }

  return (
    <div className="cw-sender">
      {modo === 'chave' ? (
        <div className="cw-linha">
          <button
            type="button"
            className={`cw-chave ${enviando ? 'tx' : ''}`}
            disabled={!podeTx}
            aria-pressed={enviando}
            aria-label={enviando ? t.cwSender.transmitindoSolte : t.cwSender.segureParaTransmitir}
            onPointerDown={apertarChave}
            onPointerUp={soltarChave}
            onPointerCancel={soltarChave}
            onLostPointerCapture={soltarChave}
            onContextMenu={(e) => e.preventDefault()}
          >
            {enviando ? t.cwSender.noAr : t.cwSender.chave}
          </button>
          <button
            type="button"
            className="cw-wpm"
            onClick={proximoWpm}
            disabled={enviando}
            aria-label={`${t.cwSender.velocidade} ${wpm} ${t.cwSender.palavrasPorMinuto}`}
          >
            {wpm} wpm
          </button>
          {enviando && (
            <button type="button" className="cw-parar" onClick={cancelar}>
              {t.cwSender.finalizar}
            </button>
          )}
        </div>
      ) : (
        <div className="cw-linha">
          <input
            type="text"
            className="cw-texto"
            placeholder={modo === 'avancado' ? t.cwSender.placeholderAvancado : t.cwSender.placeholderSimples}
            value={texto}
            disabled={modo !== 'avancado' && enviando}
            maxLength={MAX_TEXTO}
            onChange={modo === 'avancado' ? digitarAvancado : (e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && modo === 'simples') enviar()
            }}
            aria-label={t.cwSender.textoParaCW}
          />
          <button
            type="button"
            className="cw-wpm"
            onClick={proximoWpm}
            disabled={enviando}
            aria-label={`${t.cwSender.velocidade} ${wpm} ${t.cwSender.palavrasPorMinuto}`}
          >
            {wpm} wpm
          </button>
          {enviando ? (
            <button type="button" className="cw-parar" onClick={cancelar}>
              {modo === 'avancado' ? t.cwSender.finalizar : t.cwSender.parar}
            </button>
          ) : (
            modo === 'simples' && (
              <button
                type="button"
                className="cw-enviar"
                onClick={enviar}
                disabled={!podeTx || !texto.trim()}
              >
                {t.cwSender.enviar}
              </button>
            )
          )}
        </div>
      )}

      <div className="cw-linha">
        <button
          type="button"
          className={modo === 'simples' ? 'cw-modo' : 'cw-modo ligado'}
          onClick={proximoModo}
          disabled={enviando}
          aria-label={`${t.cwSender.jeitoDeMandar} ${MODOS_ENVIO.find((m) => m.id === modo)?.nome} ${t.cwSender.toqueParaTrocar}`}
        >
          {MODOS_ENVIO.find((m) => m.id === modo)?.nome}
        </button>
      </div>

      {modo !== 'simples' && <CWEnviado />}

      {!podeTx && (
        <p className="dock-bloqueio">
          {state.channel?.deny_reason ?? t.cwSender.semPrivilegio}
        </p>
      )}

      <details className="cw-ref">
        <summary>{t.cwSender.alfabetoMorse}</summary>
        <div className="cw-ref-grade">
          {Object.entries(MORSE_TABLE).map(([letra, codigo]) => (
            <span key={letra}>
              <b>{letra}</b>
              {codigo}
            </span>
          ))}
        </div>
      </details>
    </div>
  )
}

/**
 * Mostra o que JÁ FOI transmitido nos modos Avançado/Chave manual — não o
 * que está digitado ou apertado, o que já efetivamente saiu no ar. Mesmo
 * padrão de ref+rAF de `CWReadout`: `rfStore.setCWSent` é caminho quente
 * (sem notificar o React), atualizado por `ControlPlane` a cada lote/letra.
 */
function CWEnviado() {
  const { t } = useI18n()
  const ref = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    let raf = 0
    let ultimo: string | null = null

    const tick = () => {
      const enviado = rfStore.snapshot().cwSent
      if (enviado !== ultimo) {
        el.textContent = enviado ? `${t.cwSender.enviado} ${enviado}` : ''
        ultimo = enviado
      }
      raf = requestAnimationFrame(tick)
    }

    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [t])

  return <p className="cw-enviado" ref={ref} />
}
